import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    IconButton,
    CircularProgress,
    Stack,
    MenuItem
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { storeKpiApi, kpiUpdateApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';

const AddKpiModal = ({ open, handleClose, onRefresh, editKpi }) => {
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        status: 'active'
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open && editKpi) {
            setFormData({
                name: editKpi.name || '',
                code: editKpi.code || '',
                description: editKpi.description || '',
                status: editKpi.status || 'active'
            });
        } else if (open && !editKpi) {
            setFormData({ name: '', code: '', description: '', status: 'active' });
        }
    }, [open, editKpi]);

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'KPI Name is required';
        } else if (formData.name.length > 25) {
            newErrors.name = 'KPI Name must be 25 characters or below';
        }

        if (!formData.code.trim()) {
            newErrors.code = 'KPI Code is required';
        } else if (formData.code.length > 5) {
            newErrors.code = 'KPI Code must be 5 characters or below';
        } else if (!/^[A-Z0-9]+$/.test(formData.code)) {
            newErrors.code = 'KPI Code must only contain capital alphabets and numbers';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        } else if (formData.description.length > 100) {
            newErrors.description = 'Description must be 100 characters or below';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'code') {
            processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (processedValue.length > 5) return;
        }

        if (name === 'name' && value.length > 25) return;
        if (name === 'description' && value.length > 100) return;

        setFormData((prev) => ({ ...prev, [name]: processedValue }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);

        try {
            const payload = editKpi ? { ...formData, id: editKpi.id } : formData;
            const response = editKpi ? await kpiUpdateApi(payload) : await storeKpiApi(payload);

            if (response?.status === 200 || response?.data?.success) {
                setFormData({ name: '', code: '', description: '', status: 'active' });
                if (onRefresh) onRefresh();
                handleClose();
            }
        } catch (error) {
            handleCatchError(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)'
                }
            }}
        >
            <DialogTitle sx={{ p: 0 }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                        px: 3,
                        py: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: '#fff'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                bgcolor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: 'inset 0 0 10px rgba(255,255,255,0.1)'
                            }}
                        >
                            {/* <AddChartOutlined sx={{ color: '#fff', fontSize: 28 }} /> */}
                        </Box>
                        <Box>
                            <Typography color="white" variant="h5" fontWeight={800} lineHeight={1.2}>
                                {editKpi ? 'Update KPI' : 'Create New KPI'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                                Professional Performance Indicator
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        <CloseOutlined />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2, pb: 4 }}>
                <Stack spacing={4} sx={{ mt: 4 }}>
                    <TextField
                        fullWidth
                        label="KPI Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        placeholder="e.g. Sales Conversion Rate"
                        inputProps={{ maxLength: 25 }}
                        InputProps={{
                            sx: { borderRadius: 2.5, bgcolor: '#f8fafc' }
                        }}
                    />

                    <TextField
                        fullWidth
                        label="KPI Code"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        error={!!errors.code}
                        helperText={errors.code}
                        placeholder="e.g. SCR"
                        inputProps={{ maxLength: 5, style: { textTransform: 'uppercase' } }}
                        InputProps={{
                            sx: { borderRadius: 2.5, bgcolor: '#f8fafc' }
                        }}
                    />

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        error={!!errors.description}
                        helperText={errors.description}
                        placeholder="Provide details about this performance metric..."
                        inputProps={{ maxLength: 100 }}
                        InputProps={{
                            sx: { borderRadius: 2.5, bgcolor: '#f8fafc' }
                        }}
                    />

                    <TextField
                        fullWidth
                        select
                        label="Status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        InputProps={{
                            sx: { borderRadius: 2.5, bgcolor: '#f8fafc' }
                        }}
                    >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </TextField>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 4, pt: 0, gap: 2 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 700,
                        px: 3,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                    }}
                >
                    Discard
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        px: 5,
                        py: 1.5,
                        borderRadius: 2.5,
                        textTransform: 'none',
                        fontWeight: 800,
                        fontSize: '1rem',
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                        boxShadow: '0 8px 20px rgba(37, 99, 235, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                            boxShadow: '0 10px 25px rgba(37, 99, 235, 0.4)'
                        },
                        '&.Mui-disabled': {
                            background: 'rgba(0,0,0,0.1)',
                            color: 'rgba(0,0,0,0.3)'
                        }
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : editKpi ? 'Update KPI' : 'Create KPI'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AddKpiModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
    editKpi: PropTypes.object
};

export default AddKpiModal;
